import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { serverHost, activeSessionClaims } from '../http_common.js';
import { startStaffAction } from '../store/redux/universeStaffReducer.js';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LockOutlineRoundedIcon from '@mui/icons-material/LockOutlined';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { COO_WORKSPACE_PATH, hasCOOSession } from './cooAuth.js';
import './styles/cooThemeTokens.css';
import './styles/cooLogin.css';

const LOGIN_URL = `${serverHost}/universe-staff/authentication/login`;
const VERIFY_URL = `${serverHost}/universe-staff/authentication/verify-otp`;

export default function COOLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector(state => state.universeStaffs);
  const requestController = useRef(null);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => () => requestController.current?.abort(), []);
  if (hasCOOSession(session)) return <Navigate to={COO_WORKSPACE_PATH} replace />;

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    setError('');
    if (!challengeId && (!identifier.trim() || !password)) return setError('Enter your COO staff ID and password.');
    if (challengeId && !/^\d{6}$/.test(code)) return setError('Enter the six-digit authenticator code.');
    const controller = new AbortController();
    requestController.current = controller;
    setBusy(true);
    try {
      const response = await axios.post(challengeId ? VERIFY_URL : LOGIN_URL,
        challengeId ? { challengeId, code } : { user_name: identifier.trim(), stud_password: password },
        { signal: controller.signal, timeout: 30000 });
      const result = response.data;
      if (result?.status === 202 && result?.challengeId) {
        setChallengeId(result.challengeId);
        setCode(''); setPassword('');
        return;
      }
      if (result?.status !== 200) {
        setError(result?.message || 'COO access is not available for this account.');
        return;
      }
      const claims = activeSessionClaims(result?.sessionToken, ['STAFF_ACCESS']);
      if (result?.status !== 200 || claims?.role !== 'COO' || claims?.source !== 'universe' ||
          String(result?.staffData?.role?.name || '').toUpperCase() !== 'COO') {
        setError('These credentials are not authorized for the COO workspace.');
        setChallengeId(null);
        return;
      }
      dispatch(startStaffAction({ staffPayload: { staffData: result.staffData },
        authenticationType: result.authenticationType, sessionToken: result.sessionToken }));
      navigate(COO_WORKSPACE_PATH, { replace: true });
    } catch (failure) {
      if (axios.isCancel(failure)) return;
      setError(failure.response?.data?.message || 'Unable to sign in. Check your credentials and connection.');
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  }

  return <main className="coo coo-login">
    <section className="coo-login__story">
      <div className="coo-brand"><span className="coo-brand__mark">U</span><span>UNIVERSE <strong>OPERATIONS</strong></span></div>
      <div className="coo-login__story-main"><span className="coo-eyebrow">EXECUTIVE OPERATIONS PORTAL</span>
        <h1>Turn strategy<br /><em>into execution.</em></h1>
        <p>A focused environment for service delivery, regional execution, workforce alignment and operational accountability.</p>
        <div className="coo-login__chips"><span>Operational clarity</span><span>Accountable delivery</span><span>Secure leadership</span></div>
      </div>
      <p className="coo-login__foot"><VerifiedUserRoundedIcon /> Restricted executive access</p>
    </section>
    <section className="coo-login__form-area"><form className="coo-login__form" onSubmit={submit} aria-busy={busy}>
      <span className="coo-eyebrow">CHIEF OPERATING OFFICER</span>
      <h2>{challengeId ? 'Verify your identity' : 'Welcome to operations'}</h2>
      <p>{challengeId ? 'Enter your six-digit authenticator code.' : 'Sign in with your provisioned COO staff credentials.'}</p>
      {error && <div className="coo-error" role="alert">{error}</div>}
      {!challengeId ? <>
        <label htmlFor="coo-id">Official staff ID or email</label>
        <input id="coo-id" autoComplete="username" value={identifier} onChange={e=>setIdentifier(e.target.value)} required disabled={busy} />
        <label htmlFor="coo-password">Password</label>
        <div className="coo-password"><LockOutlineRoundedIcon /><input id="coo-password" type={visible?'text':'password'} autoComplete="current-password" value={password} onChange={e=>setPassword(e.target.value)} required disabled={busy}/>
          <button type="button" aria-label={visible?'Hide password':'Show password'} onClick={()=>setVisible(v=>!v)}>{visible?<VisibilityOffRoundedIcon/>:<VisibilityRoundedIcon/>}</button>
        </div>
      </> : <><label htmlFor="coo-otp">Authenticator code</label><input id="coo-otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,''))} required disabled={busy}/>
        <button type="button" className="coo-link" onClick={()=>{setChallengeId(null);setCode('');setPassword('');}}>Use different credentials</button></>}
      <button className="coo-primary" type="submit" disabled={busy}>{busy?'Verifying…':challengeId?'Verify and continue':'Continue securely'} <ArrowForwardRoundedIcon /></button>
      <small>This sign-in is separate from CEO and Executive Director accounts.</small>
    </form></section>
  </main>;
}
